import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';
import moment from 'moment';

import { URLS } from 'app/config/urls';
import { errorHandler } from 'app/utils/errorHandler';
import lineEndpoint from 'app/api/services/lineEndpoint';
import notification from 'app/config/notification';

import { fetchLine } from 'app/store/slices/line';

import {
    Button,
    FilledInput,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    makeStyles,
    TextField,
} from '@material-ui/core';
import Search from '@material-ui/icons/Search';

import * as Popover from '../components/PopoverMenu';
import Loading from 'app/layouts/Loading';
import ToggleActiveModal from './modals/ToggleActiveModal';
import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import SimpleAlert from '../components/SimpleAlert';

import { permissionariesColumns } from '../table-configs/columns-schemas';
import { activationPermissionaryValidationSchema } from '../form-configs/validation-schemas';

import styles from './styles';
import { useTableReducer } from '../hooks/useTableReducer';
import { usePersistentFilters } from '../hooks/usePersistentFilters';


const TODAY = moment(new Date()).format();

const PermissionariesList = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();

    const [data, actions] = useTableReducer();
    const { changePage, changePageSize, resetTable, loadData: loadPermissionaries } = actions;

    const screenIsMounted = React.useRef(false);

    const cdLinhaFromPath = match.params.cdLinha;

    const [loading, setLoading] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);

    const [modalPendencyData, setModalPendencyData] = useState(null);
    const [openPendenciesModal, setOpenPendenciesModal] = useState(false);

    const [openActivateModal, setOpenActivateModal] = useState(false);

    const [alertMessage, setAlertMessage] = useState(null);

    const [editEl, setEditEl] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [orderBy, setOrderBy] = useState('linhaPermissionario.deNomePermissionario');
    const [orderDirection, setOrderDirection] = useState('desc');


    const initialFilters = useMemo(() => ({
        campoOrdenacao: `${orderBy}`,
        direcaoOrdenacao: orderDirection,
        ...(cdLinhaFromPath ? { cdLinha: Number(cdLinhaFromPath) } : {})
    }), [cdLinhaFromPath, orderBy, orderDirection]);

    const [filters, setFilters] = usePersistentFilters(initialFilters, 'permissionaries-filters');

    const [searchTxt, setSearchTxt] = useState(filters.txtBusca);


    const open = Boolean(editEl);

    const selectedLine = useSelector((state) => state.line.selected);
    const activeUser = useSelector((state) => state.auth.registrationInfo);

    const hasMaximumPermi = React.useMemo(() => {
        return selectedLine?.boMaxPermissionarios;
    }, [selectedLine]);

    useEffect(() => {
        const loadLine = async () => {
            setLoading(true);
            if (!selectedLine) await dispatch(fetchLine(cdLinhaFromPath));
            setLoading(false);
        };
        loadLine();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectedLine, cdLinhaFromPath]);

    async function fetchPermi() {
        setLoadingTable(true);
        try {
            const params = {
                page: filters.page ? filters.page : data.page,
                size: filters.pageSize ? filters.pageSize : data.pageSize,
                request: filters
            };
            const response = await lineEndpoint.getPermissionaries(params);
            if (response.status === 200) {
                loadPermissionaries(response.data);
            }
        }catch (err){
            errorHandler(err.response?.status, err.response?.data?.message);
        }finally {
            if (screenIsMounted.current) setLoadingTable(false);
        }
    }

    useEffect(() => {
        screenIsMounted.current = true;
        fetchPermi();
        return () => {
            screenIsMounted.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.page, data.pageSize, filters]);

    const handleNewPermissionaryButton = () => {
        if (hasMaximumPermi) {
            setAlertMessage('O número máximo de permissionários para essa linha já foi atingido.');
        } else {
            history.push(generatePath(URLS.LINES_MANAGE_PERMISSIONARIES_NEW, { cdLinha: cdLinhaFromPath }));
        }
    };

    const handleGoBack = () => {
        history.push(generatePath(URLS.LINES_MANAGE));
    };

    const handleOpenEditMenu = (event, data) => {
        setEditEl(event.currentTarget);
        setSelectedEntry(() => data);
    };

    const handleCloseEditMenu = () => {
        setEditEl(null);
    };

    const handleOpenDetailsModal = async (event, data) => {
        setModalPendencyData(() => data);
        setOpenPendenciesModal(true);
    };

    const handleCloseModal = () => {
        setModalPendencyData(null);
        setOpenPendenciesModal(false);
    };

    const handleSubmitToggleActivation = async (values) => {
        try {
            setLoading(true);

            const isActivating = values.boAtivo === false;

            const payload = {
                boAtivar: !values.boAtivo,
                cdLinhaPermissionario: values.cdLinhaPermissionario,
                cdUsuarioResponsavel: activeUser.cdUsuario,
                deMotivo: values?.deMotivo || '',
                deObservacao: values?.deObservacao || '',
                dtCadastro: values?.dtCadastro || TODAY,
            };

            const response = await lineEndpoint.inactivatePermissionary(payload);

            if (response.status === 200) {
                await dispatch(fetchLine(cdLinhaFromPath));
                await fetchPermi();
                notification(`Permissionário ${isActivating ? 'ativado' : 'inativado'} com sucesso`, {
                    toastId: 'PERMI_ACTIVATION',
                    type: 'success',
                });
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClickToggleActivation = () => {
        const isActivating = selectedEntry?.boAtivo === false;
        if (isActivating && hasMaximumPermi) {
            setEditEl(null);
            setAlertMessage('O limite de permissionários ativos para a linha foi atingido.');
            return;
        }

        setOpenActivateModal(true);
        setEditEl(null);
    };

    const handleChangePage = (e, page) => {
        changePage(page);
        const temp = { ...filters };
        temp['page'] = page;
        setFilters(temp);
        console.log({ page });
    };

    const handleChangePageSize = (e) => {
        let pageSize = parseInt(e.target.value);
        changePageSize(pageSize);
        const temp = { ...filters };
        temp['pageSize'] = pageSize;
        setFilters(temp);
    };

    const handleTextSearch = () => {
        handleChangeFilter('txtBusca', searchTxt);
    };

    const handleChangeFilter = (key, value) => {
        resetTable();
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleChangeOrder = (event, property) => {
        let tempDirection = orderDirection;
        if (property === orderBy) {
            tempDirection = orderDirection === 'desc' ? 'asc' : 'desc';
        }
        const temp = { ...filters };
        temp['campoOrdenacao'] = `linhaPermissionario.${property}`;
        if (property === 'cdCondutorSubstituto') {
            temp['campoOrdenacao'] = `condutorSubstituto.deNome`;
        }
        temp['direcaoOrdenacao'] = tempDirection;
        setOrderDirection(tempDirection);
        setOrderBy(property);
        setFilters(temp);
        changePage(0);
    };

    return (
        <>
            <PageWrapper
                title="Lista de permissionários"
                actionButton={
                    <Button
                        disabled={hasMaximumPermi || loadingTable}
                        variant="contained"
                        color="primary"
                        disableElevation
                        className={classes.addButton}
                        onClick={handleNewPermissionaryButton}>
                        Novo permissionário
                    </Button>
                }>
                {loading && <Loading />}
                <Grid container spacing={4}>
                    <Grid item xs={12} container>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                label="Linha"
                                name="desc"
                                value={selectedLine?.deDescricao}
                                disabled
                                variant="filled"
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={2} />
                        <Grid item xs={3}>
                            <FormControl size="small" variant="filled" fullWidth>
                                <InputLabel>Buscar</InputLabel>
                                <FilledInput
                                    id="outlined-adornment-weight"
                                    value={searchTxt}
                                    onBlur={() => {handleTextSearch(true) }}
                                    onChange={(e) => setSearchTxt(e.target.value)}
                                    endAdornment={
                                        <InputAdornment position="end"  onClick={() => handleTextSearch()} style={{cursor: 'pointer'}}>
                                            <Search />
                                        </InputAdornment>
                                    }
                                    aria-describedby="outlined-weight-helper-text"
                                    inputProps={{
                                        'aria-label': 'weight',
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <DefaultTable
                            loading={loadingTable}
                            columns={permissionariesColumns}
                            rows={data.content}
                            onMore={handleOpenEditMenu}
                            entryProps={{
                                onOpenDetails: handleOpenDetailsModal,
                                invertIsActive: true,
                                otherPendenciesField: 'dePendenciasSistema',
                            }}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: data.count,
                                rowsPerPage: filters.pageSize ? filters.pageSize : data.pageSize,
                                page: filters.page ? filters.page : data.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            firstButtonLabel="Voltar"
                            onCancel={handleGoBack}
                            middleButtonLabel={null}
                            lastButtonLabel={null}
                            removeSpacing
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
            <Popover.PermissionariesList
                onEdit={handleOpenEditMenu}
                onClickActivate={handleClickToggleActivation}
                isActive={selectedEntry?.boAtivo}
                open={open}
                anchorEl={editEl}
                handleClose={handleCloseEditMenu}
                dataPath={`${URLS.LINES_MANAGE}/${match.params.cdLinha}/permissionaries`}
                dataId={selectedEntry?.cdLinhaPermissionario}
                data={selectedEntry}
            />
            <SimpleAlert title="Pendências" onClose={handleCloseModal} open={openPendenciesModal} buttonLabel="Fechar">
                <>
                    <ul>
                        {modalPendencyData?.dePendenciasSistema &&
                            modalPendencyData?.dePendenciasSistema.map((pendencia, index) => <li key={index}>{pendencia} </li>)}
                        {modalPendencyData?.dePendencias && <li>{modalPendencyData?.dePendencias}</li>}
                    </ul>
                </>
            </SimpleAlert>
            <SimpleAlert open={!!alertMessage} onClose={() => setAlertMessage(null)}>
                {alertMessage}
            </SimpleAlert>
            <ToggleActiveModal
                dateFieldEnabled
                isCurrentActive={selectedEntry?.boAtivo}
                open={openActivateModal}
                onClose={() => setOpenActivateModal(false)}
                data={selectedEntry}
                onSubmit={handleSubmitToggleActivation}
                formValidationSchema={activationPermissionaryValidationSchema}
                formInitialValues={{
                    cdLinha: selectedEntry?.cdLinha,
                    deMotivo: '',
                    deObservacao: '',
                    dtCadastro: TODAY,
                }}
            />
        </>
    );
};

const useStyles = makeStyles(styles);

export default PermissionariesList;
